"use client";

import type { ReactElement } from "react";
import type { ControllerRenderProps, FieldPath } from "react-hook-form";
import type {
  CareRecipient,
  CareSeekerLeadFormData,
} from "~care/types/forms/care-seeker-lead-form";
import type { CareSeekerShallowLeadFormData } from "~care/types/forms/care-seeker-shallow-lead-form";

import { track } from "@homeinstead/analytics";
import { EventName, scrollToTop } from "@homeinstead/shared";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { Button } from "~care/components/ui/Button/Button";
import { ServiceRecipientTrackedClickEvent } from "~care/types/events";

export const CareRecipientButton = <
  T extends CareSeekerShallowLeadFormData | CareSeekerLeadFormData,
>({
  field,
  careRecipient,
  nextStepCallback,
  className,
  children,
}: {
  field: ControllerRenderProps<T, FieldPath<T>>;
  careRecipient: CareRecipient;
  nextStepCallback: () => void;
  className: string;
  children: ReactElement | ReactElement[];
}) => {
  const pagePath = usePathname();

  // This is a workaround to prevent the button from reading the window object on the server.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    // TODO stop suppressing this warning (don't replicate this!)
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsClient(true);
  }, []);

  const handleNextStep = ({
    field,
    careRecipient,
  }: {
    field: ControllerRenderProps<T, FieldPath<T>>;
    careRecipient: CareRecipient;
  }) => {
    if (!isClient) {
      console.warn("Calling handleNextStep on the server");
      return;
    }

    field.onChange(careRecipient);
    track(
      EventName.TRACKED_CLICK,
      ServiceRecipientTrackedClickEvent(
        careRecipient,
        pagePath,
        window.location.href,
      ),
    );

    // Scroll to the top of the page on each new step
    scrollToTop();

    nextStepCallback();
  };

  return (
    <Button
      className={className}
      onClick={() => handleNextStep({ careRecipient, field })}
      type="button"
    >
      {children}
    </Button>
  );
};
